import React, { createContext, useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
    signInWithEmailAndPassword,
    signOut,
    sendSignInLinkToEmail,
    isSignInWithEmailLink,
    signInWithEmailLink,
    createUserWithEmailAndPassword,
    updatePassword,
    signInWithPopup,
    GoogleAuthProvider,
    FacebookAuthProvider,
    User,
    onAuthStateChanged
} from 'firebase/auth';
import { auth } from '../../../firebase';
import { IonSpinner } from '@ionic/react';

export enum AuthenticationState {
    INITIALIZING = 'INITIALIZING',
    AUTHENTICATED = 'AUTHENTICATED',
    UNAUTHENTICATED = 'UNAUTHENTICATED',
    REQUIRES_NEW_PASSWORD = 'REQUIRES_NEW_PASSWORD',
    BLOCKED = 'BLOCKED',
    MAGIC_LINK_INVALID = 'MAGIC_LINK_INVALID'
}

interface AuthContextType {
    authState: AuthenticationState;
    currentUser: User | null;
    isLoading: boolean;
    login: (email: string, password: string) => Promise<void>;
    loginWithMagicLink: (emailAddress: string) => Promise<void>;
    verifyMagicLink: () => Promise<void>;
    loginWithSocial: (provider: string) => Promise<void>;
    logout: () => Promise<void>;
    register: (credentials: { emailAddress: string; password: string }) => Promise<void>;
    changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [authState, setAuthState] = useState<AuthenticationState>(AuthenticationState.INITIALIZING);
    const [currentUser, setCurrentUser] = useState<User | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const history = useHistory();

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            setCurrentUser(user);
            setAuthState(user ? AuthenticationState.AUTHENTICATED : AuthenticationState.UNAUTHENTICATED);
            setIsLoading(false);
        });

        return () => unsubscribe();
    }, []);

    const login = async (email: string, password: string) => {
        setAuthState(AuthenticationState.INITIALIZING);
        try {
            await signInWithEmailAndPassword(auth, email, password);
            setAuthState(AuthenticationState.AUTHENTICATED);
            history.push('/');
        } catch (error) {
            setAuthState(AuthenticationState.UNAUTHENTICATED);
            throw error;
        }
    };

    const logout = async () => {
        setAuthState(AuthenticationState.INITIALIZING);
        try {
            await signOut(auth);
            setAuthState(AuthenticationState.UNAUTHENTICATED);
            history.push('/login');
        } catch (error) {
            console.error('Logout failed:', error);
            throw error;
        }
    };

    const loginWithMagicLink = async (email: string) => {
        try {
            const actionCodeSettings = {
                url: window.location.origin + '/verify-magic-link',
                handleCodeInApp: true
            };
            await sendSignInLinkToEmail(auth, email, actionCodeSettings);
            // Save the email for later use
            window.localStorage.setItem('emailForSignIn', email);
        } catch (error) {
            throw error;
        }
    };

    const verifyMagicLink = async () => {
        if (!isSignInWithEmailLink(auth, window.location.href)) {
            setAuthState(AuthenticationState.MAGIC_LINK_INVALID);
            throw new Error('Invalid magic link');
        }

        try {
            const email = window.localStorage.getItem('emailForSignIn');
            if (!email) {
                throw new Error('No email found for sign in');
            }

            await signInWithEmailLink(auth, email, window.location.href);
            window.localStorage.removeItem('emailForSignIn');
            setAuthState(AuthenticationState.AUTHENTICATED);
            history.push('/');
        } catch (error) {
            setAuthState(AuthenticationState.MAGIC_LINK_INVALID);
            throw error;
        }
    };

    const loginWithSocial = async (provider: string) => {
        setAuthState(AuthenticationState.INITIALIZING);
        try {
            const authProvider = provider === 'google' 
                ? new GoogleAuthProvider()
                : new FacebookAuthProvider();
            
            await signInWithPopup(auth, authProvider);
            setAuthState(AuthenticationState.AUTHENTICATED);
            history.push('/');
        } catch (error) {
            setAuthState(AuthenticationState.UNAUTHENTICATED);
            throw error;
        }
    };

    const register = async (credentials: { emailAddress: string; password: string }) => {
        try {
            await createUserWithEmailAndPassword(auth, credentials.emailAddress, credentials.password);
            history.push('/login');
        } catch (error) {
            throw error;
        }
    };

    const changePassword = async (currentPassword: string, newPassword: string) => {
        if (!currentUser) {
            throw new Error('No user is currently signed in');
        }

        try {
            // Re-authenticate user if needed
            await signInWithEmailAndPassword(auth, currentUser.email!, currentPassword);
            await updatePassword(currentUser, newPassword);
            setAuthState(AuthenticationState.AUTHENTICATED);
            history.push('/');
        } catch (error) {
            throw error;
        }
    };

    if (isLoading) {
        return (
            <div className="ion-text-center ion-padding" style={{ marginTop: '40vh' }}>
                <IonSpinner name="crescent" />
                <p>Authenticating...</p>
            </div>
        );
    }

    return (
        <AuthContext.Provider
            value={{
                authState,
                currentUser,
                isLoading,
                login,
                loginWithMagicLink,
                verifyMagicLink,
                loginWithSocial,
                logout,
                register,
                changePassword
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}; 